import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline'
type Size = 'sm' | 'md' | 'lg' | 'icon'

const variants: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 active:from-cyan-500 active:to-cyan-600 text-zinc-950 font-semibold ' +
    'shadow-[0_1px_0_0_rgba(255,255,255,0.25)_inset,0_-1px_0_0_rgba(0,0,0,0.15)_inset,0_4px_16px_-4px_rgba(34,211,238,0.55),0_0_0_1px_rgba(34,211,238,0.35)] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'bg-zinc-900/80 hover:bg-zinc-800/90 active:bg-zinc-900 text-zinc-100 border border-zinc-700/60 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'hover:bg-zinc-800/60 active:bg-zinc-800/80 text-zinc-400 hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed',
  destructive:
    'bg-gradient-to-b from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-white shadow-[0_4px_16px_-4px_rgba(244,63,94,0.55),0_0_0_1px_rgba(244,63,94,0.35)] disabled:opacity-50 disabled:cursor-not-allowed',
  outline:
    'border border-zinc-700 hover:border-cyan-500/60 hover:bg-cyan-500/5 text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed',
}

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1',
  md: 'h-9 px-3.5 text-[13px] gap-1.5',
  lg: 'h-10 px-5 text-sm gap-2',
  icon: 'h-9 w-9 grid place-items-center',
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  type = 'button',
  ...rest
}: Props) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium tracking-[-0.005em]',
        'transition-[background-color,border-color,box-shadow,transform,filter] duration-150 ease-out',
        'active:scale-[0.98] focus-visible:outline-none',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
